"use client"

import { useEffect, useRef, useState } from "react"

/**
 * Renders a HubSpot form for the `hubspotForm` content block.
 *
 * The whole point of this component is the script tag. HubSpot's embed is a
 * loader script plus an empty `<div class="hs-form-frame">` carrying the ids;
 * the loader finds the frame and fills it with an iframe. Pasting that snippet
 * into an HTML block cannot work, because every raw-HTML path we have ends in
 * `innerHTML` and the HTML parser marks scripts created that way as already
 * started — they are never fetched. Here we append the script through
 * `document.createElement`, which executes normally.
 *
 * Ordering is the other half of it: the effect below runs after mount, so the
 * frame div is already in the document when the loader first executes and there
 * is no race to lose. React owns that div but never renders children into it, so
 * it leaves HubSpot's injected iframe alone — the same arrangement
 * `EmbedWithFallback` relies on.
 */

/** HubSpot data centres ("hublets"). Both the loader and the form iframe are region-scoped. */
export type HubspotRegion = "eu1" | "na1" | "na2" | "na3" | "ap1"

/**
 * How long to wait before deciding the form is not coming. Generous on purpose:
 * a slow connection loading the script, the frame and the form definition can
 * take several seconds, and flashing a "blocked" notice at a reader whose form
 * is merely slow is worse than showing nothing for a moment longer.
 */
const BLOCKED_AFTER_MS = 8000

/** Absolute floor, so a nonsense `minHeight` can never collapse the form. */
const MIN_RENDERED_PX = 50

/**
 * Used when the block carries no `minHeight` (older documents authored before
 * the field existed). Comfortably clears a short email-capture form.
 */
const DEFAULT_MIN_HEIGHT = 560

/**
 * Globals the embed script owns, and clearing them is the only way to get a
 * frame mounted a second time.
 *
 * The loader scans for `.hs-form-frame` once per execution and records what it
 * mounted. On a re-run it finds its own registry entry and skips the frame as
 * already handled — even though React has since destroyed the node that entry
 * points at. So after a client-side navigation away and back, the new frame is
 * never filled and the page shows no form. Re-appending the script changes
 * nothing on its own, cache-busted or not: verified against the preview
 * deployment, where `numAttemptedScriptInits` climbed 1 → 2 → 3 with no iframe
 * each time. Clearing these first makes it rebuild.
 *
 * There is no supported alternative. A form object from `getForms()` exposes
 * only field getters and setters — no destroy, no remount — and the registry
 * lives behind `__SECRET_INTERNAL_DO_NOT_USE`, which is not something to build
 * on. If HubSpot ever ships a teardown, that is what should replace this.
 */
const EMBED_GLOBALS = ["HubspotFormsV4", "__HS__FORMS__EMBED__"] as const

function embedScriptUrl(region: string, portalId: string): string {
  return `https://js-${region}.hsforms.net/forms/embed/${portalId}.js`
}

/** Whether any frame on the page is still waiting for its iframe. */
function framesAwaitingMount(): boolean {
  return Array.from(document.querySelectorAll<HTMLElement>(".hs-form-frame")).some(
    (el) => !el.querySelector("iframe"),
  )
}

function clearEmbedState(): void {
  for (const key of EMBED_GLOBALS) {
    try {
      // `Reflect.deleteProperty` rather than `delete obj[key]`: same operation,
      // without tripping the dynamic-delete lint rule.
      Reflect.deleteProperty(window, key)
    } catch {
      // Non-configurable under some extensions. The re-init is still worth
      // attempting, so this is not fatal.
    }
  }
  document.querySelectorAll("script[data-hubspot-loader]").forEach((node) => {
    node.remove()
  })
}

function appendLoader(region: string, portalId: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const src = embedScriptUrl(region, portalId)
    const script = document.createElement("script")
    // Cache-busted deliberately: a browser will not re-execute a script it
    // already holds, and re-execution is the entire point — the scan happens on
    // execute. The un-busted url stays in the dataset so we can still find ours.
    script.src = `${src}?_hsInit=${Date.now()}`
    script.async = true
    script.dataset.hubspotLoader = src
    script.addEventListener("load", () => {
      resolve()
    })
    script.addEventListener("error", () => {
      // Leave nothing behind for a later attempt to wait on: a node that has
      // already fired `error` never replays it for listeners added afterwards.
      script.remove()
      reject(new Error(`blocked: ${src}`))
    })
    document.head.appendChild(script)
  })
}

/**
 * One init per commit, shared by every form mounting in it.
 *
 * Deferred to a microtask so that when a page renders several forms, all of
 * their frames are in the document before the loader scans — React runs every
 * child's effect within the same commit, so a single scan then serves all of
 * them. Without the coalescing, each form would reset the others mid-flight.
 */
let pendingInit: Promise<void> | null = null

function loadEmbedScript(region: string, portalId: string): Promise<void> {
  if (pendingInit) return pendingInit

  const run = new Promise<void>((resolve, reject) => {
    queueMicrotask(() => {
      // Nothing to do when every frame already has its iframe. Re-initialising
      // regardless would make HubSpot rebuild forms that are working, and take
      // anything a reader had typed into them with it.
      if (!framesAwaitingMount()) {
        resolve()
        return
      }

      clearEmbedState()
      appendLoader(region, portalId).then(resolve, reject)
    })
  })

  pendingInit = run
  void run
    .catch(() => undefined)
    .then(() => {
      // Cleared whether it resolved or rejected, so a later mount can try again
      // rather than inheriting a settled promise from a failed attempt.
      if (pendingInit === run) pendingInit = null
    })

  return run
}

/**
 * True once HubSpot has put its iframe inside the frame.
 *
 * Deliberately does NOT look at the iframe's height. HubSpot sets the frame's
 * inline style to `height: 0px` and the iframe to `height: 100%`, and its resize
 * handshake often never completes — so a perfectly working form measures 0 tall
 * until our own CSS floor applies. Judging "did the embed work?" by height made
 * every such form report itself as ad-blocked, which is both wrong and alarming
 * to a reader. The iframe existing is the honest signal: nothing puts it there
 * but the loader having run.
 */
function hasEmbedIframe(frame: HTMLDivElement | null): boolean {
  return Boolean(frame?.querySelector("iframe"))
}

/** How we ended up: nothing to say, the request was blocked, or it never appeared. */
type EmbedState = "pending" | "ok" | "blocked" | "absent"

export interface HubspotFormProps {
  /** `data-portal-id` from the embed code. */
  portalId: string
  /** `data-form-id` from the embed code. */
  formId: string
  /** `data-region` from the embed code. */
  region?: HubspotRegion | string
  /**
   * Height to reserve for the form, in px. Required in practice, not cosmetic:
   * see the `<style>` block below for why the form is invisible without it.
   */
  minHeight?: number
  /** HubSpot-hosted page for the same form, offered when the embed is blocked. */
  fallbackUrl?: string
  className?: string
}

export function HubspotForm({
  portalId,
  formId,
  region = "eu1",
  minHeight = DEFAULT_MIN_HEIGHT,
  fallbackUrl,
  className,
}: HubspotFormProps) {
  const frameRef = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<EmbedState>("pending")

  useEffect(() => {
    if (!portalId || !formId) return

    let cancelled = false

    loadEmbedScript(region, portalId).catch(() => {
      // An `error` event is near-conclusive: the request never completed, which
      // in practice means an extension or network policy dropped it. This is the
      // only path that lets us name an ad-blocker as the cause.
      if (!cancelled) setState("blocked")
    })

    const timer = window.setTimeout(() => {
      if (cancelled) return
      // Must not downgrade a decided `blocked`. An extension drops the request
      // almost immediately, so in the real ad-blocker case the rejection lands
      // first and this callback still fires 8s later — overwriting the accurate
      // message with the vague one, which is the common path, not an edge case.
      // `blocked` came from an actual failed request, so it outranks a deadline.
      setState((prev) =>
        prev === "blocked" ? prev : hasEmbedIframe(frameRef.current) ? "ok" : "absent",
      )
    }, BLOCKED_AFTER_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [portalId, formId, region])

  // If the embed turns up late, clear the notice.
  useEffect(() => {
    if (state === "ok" || state === "pending") return

    const poll = window.setInterval(() => {
      if (hasEmbedIframe(frameRef.current)) setState("ok")
    }, 1500)

    return () => window.clearInterval(poll)
  }, [state])

  if (!portalId || !formId) return null

  const floor = Math.max(MIN_RENDERED_PX, Math.round(minHeight))
  // Keyed on the ids rather than useId(): the value only has to be stable across
  // server and client and unique per form, and these already are.
  //
  // Stripped to selector-safe characters because this lands inside a `<style>`
  // text node, where a `"` would close the attribute selector and let stored
  // content inject arbitrary CSS. The schema's regexes already forbid that, but
  // they run in the Studio only and never over data already in the dataset —
  // the repo's rule is to enforce at the point of use, not just at authoring.
  const scope = `${portalId}-${formId}`.replace(/[^a-zA-Z0-9_-]/g, "")

  return (
    <div className={className} data-testid="hubspot-form">
      {/*
        The height floor has to come from a stylesheet, not an inline style.
        HubSpot's loader *replaces* the frame's `style` attribute with
        `height: 0px` and gives the iframe `height: 100%`, so the iframe sizes to
        the frame, the frame sizes to its content, and both settle at zero — the
        form loads correctly (HubSpot's own load-success beacon fires) and is
        still invisible. An inline style of ours would simply be overwritten;
        `min-height` in a rule survives, and clamps their `height: 0` because it
        is a different property. The iframe gets the same floor so its `100%`
        cannot resolve against a zero-height parent.
      */}
      <style>{`[data-hs-form="${scope}"],[data-hs-form="${scope}"]>iframe{min-height:${floor}px}`}</style>
      {/* Static markup, identical on server and client: HubSpot only touches it
          after hydration, so there is nothing here to mismatch. */}
      <div
        ref={frameRef}
        className="hs-form-frame"
        data-hs-form={scope}
        data-region={region}
        data-form-id={formId}
        data-portal-id={portalId}
      />
      {(state === "blocked" || state === "absent") && (
        <div
          className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="hubspot-form-fallback"
          aria-live="polite"
          role="status"
        >
          <p className="font-medium">Having trouble with the form?</p>
          <p className="mt-1 text-amber-800">
            {/* Only the `blocked` path saw a request actually fail, so it is the
                only one allowed to name a cause. Guessing "ad-blocker" at a
                reader whose form is merely slow reads as broken and blames them
                for something they have not done. */}
            {state === "blocked"
              ? "An ad-blocker or privacy extension is preventing it from loading."
              : "It is taking longer than expected to load."}
            {fallbackUrl ? (
              <>
                {" "}
                <a
                  href={fallbackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline hover:no-underline"
                  data-testid="hubspot-form-fallback-link"
                >
                  Open the form in a new tab
                </a>
                .
              </>
            ) : (
              " Please try reloading the page."
            )}
          </p>
        </div>
      )}
    </div>
  )
}
